// src/pages/Public/Store.jsx
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Form,
  Modal,
  InputGroup,
  Dropdown,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-hot-toast";
import axios from "../../api/axios";

const Store = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [priceRange, setPriceRange] = useState({
    min: 0,
    max: 1000000000000000000000,
  });
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // ✅ État initialisé avec les infos de l'utilisateur connecté
  const [checkoutData, setCheckoutData] = useState({
    shipping_name: "",
    shipping_address: "",
    shipping_phone: "",
    shipping_email: "",
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    loadCart();
  }, []);

  // ✅ Charger les infos utilisateur quand l'utilisateur est connecté
  useEffect(() => {
    if (isAuthenticated && user) {
      setCheckoutData({
        shipping_name:
          `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        shipping_address: user.address || "",
        shipping_phone: user.phone || "",
        shipping_email: user.email || "",
      });
    }
  }, [isAuthenticated, user]);

  // ✅ Mettre à jour quand le panier est ouvert (au cas où les infos ont changé)
  useEffect(() => {
    if (showCart && isAuthenticated && user) {
      // Fusionner avec les données existantes (ne pas écraser si déjà rempli)
      setCheckoutData((prev) => ({
        shipping_name:
          prev.shipping_name ||
          `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        shipping_address: prev.shipping_address || user.address || "",
        shipping_phone: prev.shipping_phone || user.phone || "",
        shipping_email: prev.shipping_email || user.email || "",
      }));
    }
  }, [showCart, isAuthenticated, user]);

  useEffect(() => {
    let filtered = [...products];
    if (searchTerm) {
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (p) =>
          p.category === selectedCategory ||
          p.category?.id === parseInt(selectedCategory),
      );
    }
    filtered = filtered.filter(
      (p) => p.price_fcfa >= priceRange.min && p.price_fcfa <= priceRange.max,
    );

    switch (sortBy) {
      case "price_asc":
        filtered.sort((a, b) => a.price_fcfa - b.price_fcfa);
        break;
      case "price_desc":
        filtered.sort((a, b) => b.price_fcfa - a.price_fcfa);
        break;
      case "name_asc":
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name_desc":
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        filtered.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
    }
    setFilteredProducts(filtered);
  }, [searchTerm, selectedCategory, sortBy, priceRange, products]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    if (imagePath.startsWith("/media/"))
      return `https://chineafriquecargo.com${imagePath}`;
    return `https://chineafriquecargo.com/media/store/products/${imagePath.split("/").pop()}`;
  };

  const getAllProductImages = (product) => {
    const images = [];
    if (product.all_images?.length) return product.all_images;
    if (product.main_image_url) images.push(product.main_image_url);
    else if (product.main_image) images.push(getImageUrl(product.main_image));
    if (product.additional_images_urls?.length)
      images.push(...product.additional_images_urls);
    else if (product.additional_images?.length)
      product.additional_images.forEach((img) => images.push(getImageUrl(img)));
    return images.filter((img, i, self) => img && self.indexOf(img) === i);
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/store/products/");
      let productsData = [];
      if (response.data?.results) productsData = response.data.results;
      else if (Array.isArray(response.data)) productsData = response.data;
      setProducts(productsData);
      setFilteredProducts(productsData);
    } catch (err) {
      console.error("Erreur chargement produits:", err);
      toast.error("Erreur chargement produits");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get("/store/categories/");
      let categoriesData = [];
      if (response.data?.results) categoriesData = response.data.results;
      else if (Array.isArray(response.data)) categoriesData = response.data;
      setCategories(categoriesData);
    } catch (err) {
      console.error("Erreur chargement catégories:", err);
      setCategories([]);
    }
  };

  const loadCart = () => {
    const savedCart = localStorage.getItem("store_cart");
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        setCart(Array.isArray(parsedCart) ? parsedCart : []);
      } catch {
        setCart([]);
      }
    }
  };

  const saveCart = (newCart) => {
    setCart(newCart);
    localStorage.setItem("store_cart", JSON.stringify(newCart));
  };

  const addToCart = (product) => {
    const existingItem = cart.find((item) => item.id === product.id);
    if (existingItem) {
      saveCart(
        cart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        ),
      );
    } else {
      saveCart([...cart, { ...product, quantity: 1 }]);
    }
    toast.success(`${product.name} ajouté au panier`);
  };

  const removeFromCart = (productId) => {
    saveCart(cart.filter((item) => item.id !== productId));
    toast.success("Produit retiré du panier");
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) return removeFromCart(productId);
    saveCart(
      cart.map((item) =>
        item.id === productId ? { ...item, quantity } : item,
      ),
    );
  };

  const getCartTotal = () =>
    cart.reduce((total, item) => total + item.price_fcfa * item.quantity, 0);
  const getCartItemCount = () =>
    cart.reduce((count, item) => count + item.quantity, 0);

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast.error("Veuillez vous connecter pour passer commande");
      navigate("/login");
      return;
    }
    if (cart.length === 0) {
      toast.error("Votre panier est vide");
      return;
    }

    // ✅ Validation
    if (!checkoutData.shipping_name?.trim()) {
      toast.error("Veuillez entrer votre nom complet");
      return;
    }
    if (!checkoutData.shipping_phone?.trim()) {
      toast.error("Veuillez entrer votre numéro WhatsApp");
      return;
    }
    if (!checkoutData.shipping_address?.trim()) {
      toast.error("Veuillez entrer votre adresse de livraison");
      return;
    }

    const orderData = {
      shipping_name: checkoutData.shipping_name,
      shipping_address: checkoutData.shipping_address,
      shipping_phone: checkoutData.shipping_phone,
      shipping_email: checkoutData.shipping_email || user?.email,
      items: cart.map((item) => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price_fcfa,
      })),
      subtotal: getCartTotal(),
      shipping_cost: 0,
      total_amount: getCartTotal(),
      payment_method: "offline",
    };

    try {
      await axios.post("/store/orders/create/", orderData);
      toast.success("✅ Commande créée avec succès !");
      saveCart([]);
      setShowCart(false);
      navigate("/client");
    } catch (err) {
      console.error("Erreur commande:", err);
      toast.error(
        err.response?.data?.message ||
          "Erreur lors de la création de la commande",
      );
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "60vh" }}
      >
        <Spinner animation="border" style={{ color: "#C62828" }} />
      </div>
    );
  }

  return (
    <>
      {/* Hero Section Ultra Moderne */}
      <div className="hero-store">
        <div className="hero-overlay"></div>
        <Container className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-pulse"> Commande</span>
            </div>
            <h1 className="hero-title">
              <span className="text-gradient">CAC Store</span>
            </h1>
            <p className="hero-subtitle">
              Découvrez une sélection unique de produits de qualité
            </p>
            <div className="search-wrapper">
              <InputGroup className="search-input-group">
                <Form.Control
                  type="text"
                  placeholder="Rechercher un produit..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <Button className="search-btn">🔍 Rechercher</Button>
              </InputGroup>
            </div>
          </div>
        </Container>
        <div className="hero-wave">
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 64L60 69.3C120 75 240 85 360 80C480 75 600 53 720 48C840 43 960 53 1080 58.7C1200 64 1320 64 1380 64L1440 64L1440 120L1380 120C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120L0 120Z"
              fill="white"
            />
          </svg>
        </div>
      </div>

      <Container className="store-container">
        {/* Header avec panier */}
        <div className="store-header">
          <div>
            <h2 className="store-title">Nos Produits</h2>
            <p className="store-subtitle">
              {filteredProducts.length} produits disponibles
            </p>
          </div>
          <div className="header-actions">
            <Dropdown>
              <Dropdown.Toggle
                variant="outline-secondary"
                className="sort-btn rounded-pill"
              >
                {sortBy === "newest" && "📅 Plus récents"}
                {sortBy === "price_asc" && "💰 Prix croissant"}
                {sortBy === "price_desc" && "💰 Prix décroissant"}
                {sortBy === "name_asc" && "🔤 Nom A-Z"}
                {sortBy === "name_desc" && "🔤 Nom Z-A"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setSortBy("newest")}>
                  📅 Plus récents
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSortBy("price_asc")}>
                  💰 Prix croissant
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSortBy("price_desc")}>
                  💰 Prix décroissant
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSortBy("name_asc")}>
                  🔤 Nom A-Z
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSortBy("name_desc")}>
                  🔤 Nom Z-A
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Button className="cart-btn" onClick={() => setShowCart(true)}>
              🛒 Panier
              {getCartItemCount() > 0 && (
                <span className="cart-badge">{getCartItemCount()}</span>
              )}
            </Button>
          </div>
        </div>

        {/* Categories */}
        <div className="categories-wrapper">
          <div className="categories-scroll">
            <button
              className={`category-chip ${selectedCategory === "all" ? "active" : ""}`}
              onClick={() => setSelectedCategory("all")}
            >
              Tous
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`category-chip ${selectedCategory === cat.id ? "active" : ""}`}
                onClick={() => setSelectedCategory(cat.id)}
              >
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h4>Aucun produit trouvé</h4>
            <p>Essayez de modifier vos critères de recherche</p>
            <Button
              variant="outline-danger"
              onClick={() => {
                setSearchTerm("");
                setSelectedCategory("all");
                setPriceRange({ min: 0, max: 1000000 });
              }}
            >
              Réinitialiser les filtres
            </Button>
          </div>
        ) : (
          <div className="products-grid">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="product-card"
                onClick={() => {
                  setSelectedProduct(product);
                  setCurrentImageIndex(0);
                  setShowProductModal(true);
                }}
              >
                <div className="product-image-container">
                  <img
                    src={
                      product.main_image_url || getImageUrl(product.main_image)
                    }
                    alt={product.name}
                    className="product-image"
                    onError={(e) => {
                      e.target.src =
                        "https://via.placeholder.com/300x300?text=No+Image";
                    }}
                  />
                  {product.is_featured && (
                    <span className="featured-badge">⭐ En vedette</span>
                  )}
                  {!product.is_available && (
                    <span className="outofstock-badge">Rupture</span>
                  )}
                  <button
                    className="quick-add"
                    onClick={(e) => {
                      e.stopPropagation();
                      addToCart(product);
                    }}
                    disabled={!product.is_available}
                  >
                    +
                  </button>
                </div>
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <div className="product-price">
                    {product.price_fcfa?.toLocaleString()} FCFA
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Product Modal */}
        <Modal
          show={showProductModal}
          onHide={() => setShowProductModal(false)}
          size="lg"
          centered
          className="product-modal-custom"
        >
          {selectedProduct && (
            <>
              <Modal.Header closeButton className="border-0">
                <Modal.Title>{selectedProduct.name}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Row>
                  <Col md={6}>
                    <div className="modal-gallery">
                      <div className="modal-main-image">
                        {(() => {
                          const images = getAllProductImages(selectedProduct);
                          if (images.length > 0) {
                            return (
                              <>
                                <img
                                  src={images[currentImageIndex]}
                                  alt={selectedProduct.name}
                                />
                                {images.length > 1 && (
                                  <div className="modal-nav">
                                    <button
                                      className="nav-prev"
                                      onClick={() =>
                                        setCurrentImageIndex((prev) =>
                                          prev === 0
                                            ? images.length - 1
                                            : prev - 1,
                                        )
                                      }
                                    >
                                      ◀
                                    </button>
                                    <button
                                      className="nav-next"
                                      onClick={() =>
                                        setCurrentImageIndex((prev) =>
                                          prev === images.length - 1
                                            ? 0
                                            : prev + 1,
                                        )
                                      }
                                    >
                                      ▶
                                    </button>
                                  </div>
                                )}
                              </>
                            );
                          }
                          return <div className="no-image-modal">📦</div>;
                        })()}
                      </div>
                      {getAllProductImages(selectedProduct).length > 1 && (
                        <div className="modal-thumbnails">
                          {getAllProductImages(selectedProduct).map(
                            (img, idx) => (
                              <img
                                key={idx}
                                src={img}
                                className={`thumbnail ${idx === currentImageIndex ? "active" : ""}`}
                                onClick={() => setCurrentImageIndex(idx)}
                              />
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="modal-info">
                      <div className="modal-price">
                        {selectedProduct.price_fcfa?.toLocaleString()} FCFA
                      </div>
                      <div className="modal-availability">
                        <span
                          className={`badge ${selectedProduct.is_available ? "bg-success" : "bg-danger"}`}
                        >
                          {selectedProduct.is_available
                            ? "En stock"
                            : "Rupture"}
                        </span>
                      </div>
                      <div className="modal-description">
                        <h6>Description</h6>
                        <p>
                          {selectedProduct.description ||
                            "Aucune description disponible"}
                        </p>
                      </div>
                      <div className="modal-actions">
                        <Button
                          className="add-to-cart-btn"
                          onClick={() => {
                            addToCart(selectedProduct);
                            setShowProductModal(false);
                          }}
                          disabled={!selectedProduct.is_available}
                        >
                          🛒 Ajouter au panier
                        </Button>
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowProductModal(false)}
                          className="continue-btn"
                        >
                          Continuer mes achats
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Modal.Body>
            </>
          )}
        </Modal>

        {/* ✅ Cart Modal - Champs pré-remplis avec infos utilisateur */}
        <Modal
          show={showCart}
          onHide={() => setShowCart(false)}
          size="lg"
          centered
          className="cart-modal-custom"
        >
          <Modal.Header closeButton className="border-0">
            <Modal.Title>
              🛒 Mon panier ({getCartItemCount()} articles)
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {cart.length === 0 ? (
              <div className="empty-cart">
                <div className="empty-cart-icon">🛒</div>
                <h5>Votre panier est vide</h5>
                <Button variant="danger" onClick={() => setShowCart(false)}>
                  Continuer les achats
                </Button>
              </div>
            ) : (
              <>
                <div className="cart-items-list">
                  {cart.map((item) => (
                    <div key={item.id} className="cart-item">
                      <img
                        src={
                          item.main_image_url || getImageUrl(item.main_image)
                        }
                        alt={item.name}
                        className="cart-item-img"
                      />
                      <div className="cart-item-info">
                        <h6>{item.name}</h6>
                        <div className="cart-item-price">
                          {item.price_fcfa?.toLocaleString()} FCFA
                        </div>
                      </div>
                      <div className="cart-item-quantity">
                        <button
                          onClick={() =>
                            updateQuantity(item.id, item.quantity - 1)
                          }
                        >
                          -
                        </button>
                        <span>{item.quantity}</span>
                        <button
                          onClick={() =>
                            updateQuantity(item.id, item.quantity + 1)
                          }
                        >
                          +
                        </button>
                        <button
                          className="remove-btn"
                          onClick={() => removeFromCart(item.id)}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="cart-summary">
                  <div className="summary-row">
                    <span>Sous-total</span>
                    <strong>{getCartTotal().toLocaleString()} FCFA</strong>
                  </div>
                  <div className="summary-row">
                    <strong className="text-success">
                      Un agent de CAC vous contactera.
                    </strong>
                  </div>
                  <div className="summary-row total">
                    <span>Total</span>
                    <strong>{getCartTotal().toLocaleString()} FCFA</strong>
                  </div>
                </div>
                <hr />
                <h6>📦 Informations de livraison</h6>
                {isAuthenticated && (
                  <small className="text-muted mb-3 d-block">
                    ✅ Vos informations sont pré-remplies depuis votre profil.
                    Vous pouvez les modifier si nécessaire.
                  </small>
                )}
                <Form>
                  <Form.Group className="mb-2">
                    <Form.Label>
                      Nom complet <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ex: Jean Dupont"
                      value={checkoutData.shipping_name}
                      onChange={(e) =>
                        setCheckoutData({
                          ...checkoutData,
                          shipping_name: e.target.value,
                        })
                      }
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>
                          N° WhatsApp <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          placeholder="+237XXXXXXXXX"
                          value={checkoutData.shipping_phone}
                          onChange={(e) =>
                            setCheckoutData({
                              ...checkoutData,
                              shipping_phone: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="votre@email.com"
                          value={checkoutData.shipping_email}
                          onChange={(e) =>
                            setCheckoutData({
                              ...checkoutData,
                              shipping_email: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-2">
                    <Form.Label>
                      Adresse de livraison{" "}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Votre adresse complète (rue, ville, pays)"
                      value={checkoutData.shipping_address}
                      onChange={(e) =>
                        setCheckoutData({
                          ...checkoutData,
                          shipping_address: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Form>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => setShowCart(false)}>
              Continuer les achats
            </Button>
            {cart.length > 0 && (
              <Button variant="danger" onClick={handleCheckout}>
                ✅ Valider la commande
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </Container>

      <style>{`
                .hero-store {
                    position: relative;
                    background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
                    min-height: 500px;
                    display: flex;
                    align-items: center;
                    overflow: hidden;
                }
                
                .hero-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: radial-gradient(circle at 20% 50%, rgba(198, 40, 40, 0.2) 0%, transparent 50%);
                }
                
                .hero-container {
                    position: relative;
                    z-index: 2;
                    padding: 80px 0;
                }
                
                .hero-content {
                    text-align: center;
                    max-width: 700px;
                    margin: 0 auto;
                }
                
                .hero-badge {
                    margin-bottom: 24px;
                }
                
                .badge-pulse {
                    display: inline-block;
                    padding: 8px 20px;
                    background: rgba(249, 168, 37, 0.15);
                    border: 1px solid rgba(249, 168, 37, 0.3);
                    border-radius: 50px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #F9A825;
                    animation: pulse 2s infinite;
                }
                
                @keyframes pulse {
                    0% { box-shadow: 0 0 0 0 rgba(249, 168, 37, 0.4); }
                    70% { box-shadow: 0 0 0 10px rgba(249, 168, 37, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(249, 168, 37, 0); }
                }
                
                .hero-title {
                    font-size: 64px;
                    font-weight: 800;
                    margin-bottom: 20px;
                }
                
                .text-gradient {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
                
                .hero-subtitle {
                    font-size: 18px;
                    color: rgba(255, 255, 255, 0.7);
                    margin-bottom: 40px;
                }
                
                .search-wrapper {
                    max-width: 500px;
                    margin: 0 auto;
                }
                
                .search-input-group {
                    background: white;
                    border-radius: 60px;
                    overflow: hidden;
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
                }
                
                .search-input {
                    border: none;
                    padding: 16px 24px;
                    font-size: 16px;
                }
                
                .search-input:focus {
                    box-shadow: none;
                }
                
                .search-btn {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border: none;
                    padding: 16px 32px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                }
                
                .search-btn:hover {
                    transform: scale(1.02);
                }
                
                .hero-wave {
                    position: absolute;
                    bottom: -1px;
                    left: 0;
                    right: 0;
                }
                
                .store-container {
                    padding: 60px 0;
                }
                
                .store-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 40px;
                    flex-wrap: wrap;
                    gap: 20px;
                }
                
                .store-title {
                    font-size: 32px;
                    font-weight: 800;
                    margin-bottom: 8px;
                }
                
                .store-subtitle {
                    color: #666;
                    margin: 0;
                }
                
                .header-actions {
                    display: flex;
                    gap: 16px;
                }
                
                .sort-btn {
                    background: white;
                    border: 1px solid #e0e0e0;
                    padding: 10px 20px;
                }
                
                .cart-btn {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border: none;
                    border-radius: 40px;
                    padding: 10px 24px;
                    position: relative;
                    transition: all 0.3s ease;
                }
                
                .cart-btn:hover {
                    transform: scale(1.05);
                }
                
                .cart-badge {
                    position: absolute;
                    top: -8px;
                    right: -8px;
                    background: #F9A825;
                    color: #212121;
                    border-radius: 50%;
                    width: 22px;
                    height: 22px;
                    font-size: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                }
                
                .categories-wrapper {
                    margin-bottom: 40px;
                    overflow-x: auto;
                }
                
                .categories-scroll {
                    display: flex;
                    gap: 12px;
                    flex-wrap: wrap;
                    justify-content: center;
                }
                
                .category-chip {
                    padding: 8px 20px;
                    border-radius: 40px;
                    background: #f0f0f0;
                    border: none;
                    font-size: 14px;
                    font-weight: 500;
                    transition: all 0.3s ease;
                    cursor: pointer;
                }
                
                .category-chip:hover {
                    background: #e0e0e0;
                    transform: translateY(-2px);
                }
                
                .category-chip.active {
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    color: white;
                }
                
                .products-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 30px;
                }
                
                .product-card {
                    background: white;
                    border-radius: 20px;
                    overflow: hidden;
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    cursor: pointer;
                    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
                }
                
                .product-card:hover {
                    transform: translateY(-10px);
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
                }
                
                .product-image-container {
                    position: relative;
                    height: 280px;
                    overflow: hidden;
                    background: #f8f9fa;
                }
                
                .product-image {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.5s ease;
                }
                
                .product-card:hover .product-image {
                    transform: scale(1.1);
                }
                
                .featured-badge {
                    position: absolute;
                    top: 12px;
                    left: 12px;
                    background: #F9A825;
                    color: #212121;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: bold;
                    z-index: 1;
                }
                
                .outofstock-badge {
                    position: absolute;
                    top: 12px;
                    left: 12px;
                    background: #9e9e9e;
                    color: white;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: bold;
                    z-index: 1;
                }
                
                .quick-add {
                    position: absolute;
                    bottom: 12px;
                    right: 12px;
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    color: white;
                    border: none;
                    font-size: 24px;
                    cursor: pointer;
                    opacity: 0;
                    transform: translateY(10px);
                    transition: all 0.3s ease;
                }
                
                .product-card:hover .quick-add {
                    opacity: 1;
                    transform: translateY(0);
                }
                
                .quick-add:hover {
                    transform: scale(1.1);
                }
                
                .product-info {
                    padding: 20px;
                }
                
                .product-name {
                    font-size: 16px;
                    font-weight: 600;
                    margin-bottom: 8px;
                    color: #333;
                }
                
                .product-price {
                    font-size: 20px;
                    font-weight: bold;
                    color: #C62828;
                    margin-bottom: 8px;
                }
                
                .product-rating {
                    font-size: 13px;
                    color: #F9A825;
                }
                
                .reviews {
                    color: #999;
                    margin-left: 8px;
                }
                
                .empty-state {
                    text-align: center;
                    padding: 80px 20px;
                }
                
                .empty-icon {
                    font-size: 80px;
                    margin-bottom: 20px;
                }
                
                .product-modal-custom .modal-content {
                    border-radius: 24px;
                    overflow: hidden;
                }
                
                .modal-gallery {
                    position: relative;
                }
                
                .modal-main-image {
                    position: relative;
                    background: #f8f9fa;
                    border-radius: 16px;
                    overflow: hidden;
                    min-height: 300px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .modal-main-image img {
                    max-height: 350px;
                    width: auto;
                }
                
                .modal-nav {
                    position: absolute;
                    top: 50%;
                    left: 0;
                    right: 0;
                    transform: translateY(-50%);
                    display: flex;
                    justify-content: space-between;
                    padding: 0 10px;
                }
                
                .modal-nav button {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: rgba(0,0,0,0.5);
                    color: white;
                    border: none;
                    cursor: pointer;
                }
                
                .modal-thumbnails {
                    display: flex;
                    gap: 10px;
                    margin-top: 15px;
                    overflow-x: auto;
                }
                
                .modal-thumbnails .thumbnail {
                    width: 60px;
                    height: 60px;
                    object-fit: cover;
                    border-radius: 8px;
                    cursor: pointer;
                    opacity: 0.6;
                    transition: all 0.3s ease;
                }
                
                .modal-thumbnails .thumbnail.active,
                .modal-thumbnails .thumbnail:hover {
                    opacity: 1;
                    border: 2px solid #C62828;
                }
                
                .modal-price {
                    font-size: 32px;
                    font-weight: bold;
                    color: #C62828;
                    margin-bottom: 16px;
                }
                
                .modal-actions {
                    margin-top: 24px;
                }
                
                .add-to-cart-btn {
                    width: 100%;
                    background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
                    border: none;
                    padding: 12px;
                    border-radius: 12px;
                    font-weight: 600;
                    margin-bottom: 12px;
                }
                
                .continue-btn {
                    width: 100%;
                }
                
                .cart-modal-custom .modal-content {
                    border-radius: 24px;
                }
                
                .cart-items-list {
                    max-height: 400px;
                    overflow-y: auto;
                }
                
                .cart-item {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 16px;
                    border-bottom: 1px solid #eee;
                }
                
                .cart-item-img {
                    width: 70px;
                    height: 70px;
                    object-fit: cover;
                    border-radius: 12px;
                }
                
                .cart-item-info {
                    flex: 1;
                }
                
                .cart-item-info h6 {
                    margin-bottom: 4px;
                }
                
                .cart-item-price {
                    color: #C62828;
                    font-weight: 600;
                }
                
                .cart-item-quantity {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                
                .cart-item-quantity button {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    border: 1px solid #ddd;
                    background: white;
                    cursor: pointer;
                }
                
                .cart-item-quantity .remove-btn {
                    background: #fee;
                    border-color: #fcc;
                }
                
                .cart-summary {
                    background: #f8f9fa;
                    border-radius: 16px;
                    padding: 20px;
                    margin-top: 20px;
                }
                
                .summary-row {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 12px;
                }
                
                .summary-row.total {
                    font-size: 18px;
                    font-weight: bold;
                    border-top: 1px solid #ddd;
                    padding-top: 12px;
                    margin-top: 12px;
                }
                
                .empty-cart {
                    text-align: center;
                    padding: 60px 20px;
                }
                
                .empty-cart-icon {
                    font-size: 80px;
                    margin-bottom: 20px;
                }
                
                @media (max-width: 768px) {
                    .hero-title {
                        font-size: 40px;
                    }
                    
                    .store-title {
                        font-size: 24px;
                    }
                    
                    .products-grid {
                        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
                        gap: 20px;
                    }
                    
                    .product-image-container {
                        height: 220px;
                    }
                    
                    .header-actions {
                        width: 100%;
                        justify-content: space-between;
                    }
                }
            `}</style>
    </>
  );
};

export default Store;
